/**
 * Framework-agnostic money PARSING — the inverse of {@link formatMoney} for form
 * inputs and editable amount fields.
 *
 * A user (or a round-tripped display value) types an amount in the en-GB /
 * dot-decimal display shape — a currency symbol or ISO code prefix, comma grouping
 * separators, stray whitespace, and a dot decimal point ("£1,234.56", "GBP 1,234.56").
 * This turns that back into a plain number, or null when there is no meaningful value
 * to parse.
 *
 * It is deliberately lenient about the symbol, currency-code prefix and grouping so
 * it tolerates the display forms {@link formatMoney} produces in the en-GB fallback
 * locale, but it is NOT a locale-universal inverse of {@link formatMoney}. Only the
 * dot-decimal shape is understood: a comma is always treated as a grouping separator
 * and dropped, so comma-decimal locales (de-DE `1.234,56`) are OUT OF SCOPE and would
 * mis-parse. The platform stores and edits amounts in the dot-decimal numeric form.
 *
 * Ported from the per-plugin `parseCurrency` helper so consuming plugins drop the
 * local copy.
 */

/**
 * The Unicode minus sign (U+2212) that `Intl.NumberFormat` emits for negatives.
 * It is not the ASCII hyphen-minus, so `parseFloat` would not treat it as a sign;
 * normalise it to `-` before stripping.
 */
const UNICODE_MINUS = /−/g;

/**
 * After normalising the minus, everything that is NOT a digit, dot or minus is
 * stripped: currency symbols (£ $ € ¥ ₹ and any other), alpha ISO-code prefixes
 * (the "GBP " prefix, and the invalid-code fallback prefix like "ZZZZ"), comma
 * grouping separators, and all whitespace (including the non-breaking / narrow
 * no-break spaces `Intl` inserts).
 */
const NON_NUMERIC_CHARS = /[^0-9.-]/g;

/**
 * Parses a formatted currency string back to a number. Returns null for
 * null/undefined, an empty/whitespace-only string, or the literal `"N/A"`
 * placeholder; normalises the Unicode minus, strips every non-numeric character
 * (currency symbols, alpha currency-code prefixes, grouping commas and whitespace),
 * then `parseFloat`s the remainder, returning null when the result is not a number.
 *
 * Parses only the en-GB / dot-decimal display shape (comma thousands, dot decimal);
 * see the module docs — it is NOT a locale-universal inverse of {@link formatMoney}.
 * A leading minus is preserved, so `"-£10.00"` parses to `-10`. Garbage that contains
 * no leading number (`"abc"`) yields null.
 *
 * @example
 * parseMoney("£1,234.56")   // 1234.56
 * parseMoney("GBP 1,234.56") // 1234.56
 * parseMoney("−£10.00")     // -10 (Unicode minus U+2212)
 * parseMoney("ZZZZ10.00")   // 10  (invalid-code fallback prefix)
 * parseMoney("N/A")         // null
 * parseMoney("")            // null
 * parseMoney(null)          // null
 */
export function parseMoney(value: string | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "N/A") {
    return null;
  }

  const cleaned = trimmed.replace(UNICODE_MINUS, "-").replace(NON_NUMERIC_CHARS, "");
  const parsed = Number.parseFloat(cleaned);

  return Number.isNaN(parsed) ? null : parsed;
}
